import React, { useState, useEffect, useCallback } from 'react';
import isEmpty from 'lodash/isEmpty';

import type { View } from 'common/types/view';
import type { GuidanceSummaryV2 } from 'common/types/approvals';

import { I18n } from 'common/i18n';
import { hasOneOfRights } from 'common/views/has_rights';
import { editDatasetAsRevision, findWorkingCopyFor } from 'common/views/helpers';
import airbrake from 'common/airbrake';
import { showToastNow, ToastType } from 'common/components/ToastNotification/Toastmaster';
import { assign } from 'common/window_location';
import { containsLegacyDataTypes } from 'common/views/contains_legacy_data_types';
import { ActionItem } from '../ActionItem';
import { requireApprovalRequestWithdrawalBeforeAction } from 'common/components/AssetActionBar/components/publication_action';
import optionallyLocalizeUrls from 'common/site_chrome/app/assets/javascripts/socrata_site_chrome/utils/optionally_localize_urls';
import { isQueryJSONBased } from 'common/views/view_types';
import { PublicationStage, ViewRight } from 'common/types/view';

const isDsmpSupportedAssetType = (assetType: string): boolean => {
  return ['dataset', 'href', 'file', 'filter'].includes(assetType);
};

// assumes no working copies
const qualifiesForDSMPEdit = (assetType: string, view: View): boolean => {
  if (view.publicationStage !== PublicationStage.Published) {
    return false;
  }

  if (!isDsmpSupportedAssetType(assetType)) {
    return false;
  }

  if (assetType === 'dataset') {
    return !containsLegacyDataTypes(view);
  }

  if (assetType === 'filter') {
    return !isQueryJSONBased(view);
  }

  return true;
};

export interface EditMetadataActionItemProps {
  approvalsGuidance: GuidanceSummaryV2;
  assetType: string;
  // null means it is not a revision
  revisionInfo?: { revisionSeq: number; link: string } | null;
  view: View;
  assetIsStoryDraft?: boolean;
}

/**
 * Custom hook to manage the edit state for the EditMetadataActionItem
 */
function useEditState(revisionInfo?: { revisionSeq: number; link: string } | null) {
  const [editKey, setEditKey] = useState<string | null>(() => {
    if (revisionInfo) {
      return 'edit';
    }
    // initialize to null to indicate unfinished API call
    return null;
  });

  const [onClickEdit, setOnClickEdit] = useState<(() => void) | null>(() => {
    if (revisionInfo) {
      return () => assign(revisionInfo.link);
    }
    return null;
  });

  const [toolTipText, setToolTipText] = useState<string | undefined>(undefined);

  const setEditState = useCallback(
    (newEditKey: string | null, newOnClickEdit: (() => void) | null, newToolTipText?: string) => {
      setEditKey(newEditKey);
      setOnClickEdit(() => newOnClickEdit);
      setToolTipText(newToolTipText);
    },
    [],
  );

  return {
    editKey,
    onClickEdit,
    toolTipText,
    setEditState,
  };
}

export const EditMetadataActionItem = ({
  approvalsGuidance,
  assetType,
  revisionInfo,
  view,
  assetIsStoryDraft,
}: EditMetadataActionItemProps) => {
  const { editKey, onClickEdit, toolTipText, setEditState } = useEditState(revisionInfo);

  const editAsRevision = useCallback(async (uid: string, isDataset: boolean) => {
    try {
      await editDatasetAsRevision(uid, isDataset);
    } catch (err) {
      airbrake.notify({
        error: err,
        context: { component: 'EditMetadataActionItem#editAsRevision' },
      });
      showToastNow({
        type: ToastType.ERROR,
        content: I18n.t('shared.site_chrome.asset_action_bar.edit_error'),
      });
    }
  }, []);

  const canSetUpOnClickEdit = useCallback(() => {
    if (isEmpty(view) || isEmpty(approvalsGuidance) || view.locked) {
      return false;
    }

    return hasOneOfRights(view, ViewRight.Write, ViewRight.UpdateView);
  }, [view, approvalsGuidance]);

  useEffect(() => {
    if (!canSetUpOnClickEdit()) {
      return;
    }

    if (onClickEdit === null) {
      try {
        if (qualifiesForDSMPEdit(assetType, view)) {
          // check for working copy
          findWorkingCopyFor(view.id).then((workingCopy) => {
            if (workingCopy) {
              setEditState('edit', () => assign(`/d/${workingCopy.id}/data`), undefined);
            } else {
              setEditState('edit', () => editAsRevision(view.id, assetType === 'dataset'), undefined);
            }
          });
        } else if (view.publicationStage === 'published' && assetType !== 'story') {
          // EN-34337 Metadata cannot be edited from published measures, visualizations and non-soql filtered views
          setEditState(
            'edit_metadata',
            () => null,
            I18n.t('shared.asset_browser.result_list_table.action_dropdown.disabled_tooltip'),
          );
        } else {
          // not a dsmp situation, use /edit_metadata
          setEditState(
            'edit_metadata',
            () => assign(optionallyLocalizeUrls(`/d/${view.id}/edit_metadata`)),
            undefined,
          );
        }
      } catch (err) {
        // edit button will fail to resolve
        airbrake.notify({
          error: err,
          context: { component: 'EditMetadataActionItem#useEffect' },
        });
      }
    }
  }, [assetType, view, onClickEdit, canSetUpOnClickEdit, editAsRevision, setEditState]);

  const handleClick = useCallback(() => {
    return requireApprovalRequestWithdrawalBeforeAction(approvalsGuidance, onClickEdit!);
  }, [approvalsGuidance, onClickEdit]);

  // Don't render if user doesn't have permissions or edit action hasn't been determined yet
  if (!canSetUpOnClickEdit() || onClickEdit === null) {
    return null;
  }

  return (
    <ActionItem
      toolTipText={toolTipText}
      label={I18n.t(`shared.asset_browser.result_list_table.action_dropdown.${editKey}`)}
      onClick={handleClick}
    />
  );
};
